import { useEffect, useId, useMemo, useState } from 'react'

import Button from '@/components/ui/Button'
import Field from '@/components/ui/Field'
import Modal from '@/components/ui/Modal'
import { usePreview, useSettled } from '@/features/dashboard/api/queries'
import { CHECK_DELAY_MS, applyFix } from '@/features/dashboard/lib/formula'
import {
  KINDS,
  RANKS,
  UNITS,
  type Rank,
  type Vocabulary,
  type WidgetDraft,
  type WidgetKind,
  type WidgetUnit,
} from '@/features/mitra/api/dashboard'
import FormulaCheck from '@/screens/dashboards/components/FormulaCheck'
import FormulaInput from '@/screens/dashboards/components/FormulaInput'
import { SHOW_WORDS, WINDOW_WORDS, EVERY_WORDS } from '@/features/dashboard/lib/time'
import type {
  TimeEvery,
  TimeShow,
  TimeSpec,
  TimeVocabulary,
  TimeWindow,
} from '@/features/mitra/api/dashboard'

const KIND_WORDS: Record<WidgetKind, string> = {
  number: 'One figure for my book',
  column: 'A column in my holdings table',
  bars: 'Bars, one per holding',
  sector_bars: 'Bars, one per sector',
}

const UNIT_WORDS: Record<WidgetUnit, string> = {
  NPR: 'Rupees',
  percent: 'Percent',
  ratio: 'Plain number',
  shares: 'Kitta',
  count: 'Count',
}

const SELECT =
  'mt-1 min-h-11 w-full rounded-sm border border-border bg-background px-3 text-body-m text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring'

const RANK_WORDS: Record<Rank, string> = {
  desc: 'Largest first',
  asc: 'Smallest first',
  abs: 'Largest either way first',
}

const NOW = 'now'
const WHOLE = 'whole'

/**
 * The time dimension of the widget being edited: When (today, or one of the
 * engine's windows, a custom one by its dates), Every (how it steps; over
 * brokers also "Whole window", one figure per broker) and Show as. Over
 * brokers, which companies' brokers. Every choice comes from the
 * vocabulary's `time`; an engine with none draws none of this.
 */
function TimeFields({
  draft,
  time,
  wasWindowed,
  onChange,
}: {
  draft: WidgetDraft
  time: TimeVocabulary
  /** The widget was saved over a window: "Now" must send `time: null` to clear it. */
  wasWindowed: boolean
  onChange: (draft: WidgetDraft) => void
}) {
  const brokers = draft.scope?.kind === 'brokers'
  const spec = draft.time ?? null
  const set = (next: TimeSpec | null) => {
    onChange({ ...draft, time: next ?? (wasWindowed ? null : undefined) })
  }
  const windowOf = (key: TimeWindow): TimeSpec => ({
    window: key,
    from: key === 'custom' ? (spec?.from ?? null) : null,
    to: key === 'custom' ? (spec?.to ?? null) : null,
    every: brokers ? (spec?.every ?? null) : (time.defaultEvery[key] ?? spec?.every ?? 'day'),
    show: spec?.show ?? null,
  })
  return (
    <fieldset data-role="editor-time" className="grid grid-cols-2 gap-3 @md:grid-cols-3">
      <legend className="sr-only">Over time</legend>
      <label className="text-label-m text-muted-foreground">
        When
        <select
          data-role="editor-window"
          className={SELECT}
          value={spec?.window ?? NOW}
          onChange={(e) => {
            set(e.target.value === NOW ? null : windowOf(e.target.value as TimeWindow))
          }}
        >
          {brokers ? null : <option value={NOW}>Now</option>}
          {time.windows.map((w) => (
            <option key={w.key} value={w.key}>
              {w.label || WINDOW_WORDS[w.key]}
            </option>
          ))}
        </select>
      </label>
      {spec ? (
        <label className="text-label-m text-muted-foreground">
          Every
          <select
            data-role="editor-every"
            className={SELECT}
            value={spec.every ?? WHOLE}
            onChange={(e) => {
              set({
                ...spec,
                every: e.target.value === WHOLE ? null : (e.target.value as TimeEvery),
              })
            }}
          >
            {brokers ? <option value={WHOLE}>Whole window</option> : null}
            {time.every.map((e) => (
              <option key={e.key} value={e.key}>
                {e.label || EVERY_WORDS[e.key]}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      {spec?.every ? (
        <label className="text-label-m text-muted-foreground">
          Show as
          <select
            data-role="editor-show"
            className={SELECT}
            value={spec.show ?? 'line'}
            onChange={(e) => {
              set({ ...spec, show: e.target.value as TimeShow })
            }}
          >
            {(time.show.length ? time.show : (['line', 'bars', 'table'] as const)).map((show) => (
              <option key={show} value={show}>
                {SHOW_WORDS[show]}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      {spec?.window === 'custom' ? (
        <>
          <Field
            label="From"
            type="date"
            value={spec.from ?? ''}
            onChange={(e) => {
              set({ ...spec, from: e.target.value || null })
            }}
          />
          <Field
            label="To"
            type="date"
            value={spec.to ?? ''}
            onChange={(e) => {
              set({ ...spec, to: e.target.value || null })
            }}
          />
        </>
      ) : null}
      {brokers ? (
        <div className="col-span-full">
          <Field
            label={`Brokers in (up to ${String(time.limits.floorsheetSymbols ?? 3)} companies; empty for the whole market)`}
            data-role="editor-broker-symbols"
            value={draft.scope?.kind === 'brokers' ? draft.scope.symbols.join(', ') : ''}
            onChange={(e) => {
              const symbols = [
                ...new Set(
                  e.target.value
                    .split(/[\s,]+/)
                    .map((x) => x.trim().toUpperCase())
                    .filter(Boolean),
                ),
              ]
              onChange({ ...draft, scope: { kind: 'brokers', symbols } })
            }}
          />
        </div>
      ) : null}
    </fieldset>
  )
}

/**
 * One widget's definition, with the engine's live check-up of its formula.
 * Saving hands the draft up; this component writes nothing itself.
 *
 * The unit follows what the formula works out to, until the reader picks one
 * themselves: then it is theirs, and only a warning says otherwise.
 */
export default function WidgetEditor({
  open,
  initial,
  isNew,
  vocabulary,
  saving,
  refused,
  onSave,
  onClose,
  onAsk,
}: {
  open: boolean
  initial: WidgetDraft
  isNew: boolean
  vocabulary: Vocabulary | null
  saving: boolean
  refused: string | null
  onSave: (draft: WidgetDraft) => void
  onClose: () => void
  /** Hand a formula that does not read to Mitra; null where there is no chat. */
  onAsk?: ((question: string) => void) | null | undefined
}) {
  const [draft, setDraft] = useState<WidgetDraft>(initial)
  const [unitTouched, setUnitTouched] = useState(false)
  const wasWindowed = initial.time !== undefined && initial.time !== null
  // A custom window waits for both dates before it is checked or saved.
  const timeReady =
    draft.time?.window !== 'custom' || (Boolean(draft.time.from) && Boolean(draft.time.to))
  const id = useId()
  const names = vocabulary ?? null

  // Checked as soon as there is a formula: a reader who writes it first is the
  // one who needs its repairs most. A title is only for saving.
  const ready = draft.formula.trim() !== '' && timeReady
  const settled = useSettled(draft, CHECK_DELAY_MS)
  const asked = useMemo(
    () => [{ ...settled, title: settled.title.trim() || 'Untitled measure' }],
    [settled],
  )
  const preview = usePreview(asked, open && ready)
  const reading = settled === draft && preview.data ? preview.data : null
  const widget = reading?.widgets[0] ?? null
  const check = widget?.check ?? null
  const checking = ready && (settled !== draft || preview.isFetching)

  const detected = check?.unit ?? null
  useEffect(() => {
    if (!unitTouched && detected && detected !== draft.unit) {
      // The engine's answer arrives after render; following it is the point.
      // eslint-disable-next-line react-hooks/set-state-in-effect -- following the engine's detected unit
      setDraft((now) => ({ ...now, unit: detected }))
    }
  }, [detected, unitTouched, draft.unit])

  const save = () => {
    if (ready && !saving) onSave(draft)
  }

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
      title={isNew ? 'New measure' : 'Edit measure'}
      size="lg"
      footer={
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={saving || !ready || draft.title.trim() === ''}
            onClick={save}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      }
    >
      <form
        data-role="dash-editor"
        onSubmit={(event) => {
          event.preventDefault()
          save()
        }}
        className="flex min-w-0 flex-col gap-3"
      >
        <Field
          label="Title"
          value={draft.title}
          maxLength={60}
          onChange={(e) => {
            setDraft({ ...draft, title: e.target.value })
          }}
        />
        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-between gap-2">
            <label htmlFor={`${id}-formula`} className="text-label-m text-muted-foreground">
              Formula
            </label>
          </div>
          <FormulaInput
            id={`${id}-formula`}
            value={draft.formula}
            vocabulary={names}
            check={check}
            onChange={(formula) => {
              setDraft({ ...draft, formula })
            }}
          />
        </div>
        <FormulaCheck
          formula={draft.formula}
          checking={checking}
          check={check}
          widget={widget}
          holdings={reading?.holdings ?? []}
          scopes={reading?.scopes ?? []}
          onApply={(fix) => {
            if (fix.unit) setUnitTouched(false)
            setDraft(applyFix(draft, fix))
          }}
          onAsk={onAsk ?? undefined}
        />
        <div className="grid grid-cols-2 gap-3">
          <label className="col-span-2 text-label-m text-muted-foreground">
            Shows
            <select
              className={SELECT}
              value={draft.kind}
              onChange={(e) => {
                setDraft({ ...draft, kind: e.target.value as WidgetKind })
              }}
            >
              {KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {KIND_WORDS[kind]}
                </option>
              ))}
            </select>
          </label>
          <label className="text-label-m text-muted-foreground">
            Unit
            <select
              className={SELECT}
              value={draft.unit}
              onChange={(e) => {
                setUnitTouched(true)
                setDraft({ ...draft, unit: e.target.value as WidgetUnit })
              }}
            >
              {UNITS.map((unit) => (
                <option key={unit} value={unit}>
                  {UNIT_WORDS[unit]}
                </option>
              ))}
            </select>
          </label>
          <label className="text-label-m text-muted-foreground">
            Decimal places
            <select
              className={SELECT}
              value={draft.decimals}
              onChange={(e) => {
                setDraft({ ...draft, decimals: Number(e.target.value) })
              }}
            >
              {[0, 1, 2, 3, 4].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        </div>
        {draft.kind !== 'number' ? (
          <div data-role="editor-order" className="grid grid-cols-2 gap-3">
            <label className="text-label-m text-muted-foreground">
              Order
              <select
                data-role="editor-rank"
                className={SELECT}
                value={draft.rank ?? ''}
                onChange={(e) => {
                  const rank = e.target.value as Rank | ''
                  // Cleared as null where it was saved, so the engine drops it.
                  setDraft({
                    ...draft,
                    rank: rank === '' ? (initial.rank ? null : undefined) : rank,
                  })
                }}
              >
                <option value="">As listed</option>
                {(vocabulary?.rank.length ? vocabulary.rank : RANKS).map((rank) => (
                  <option key={rank} value={rank}>
                    {RANK_WORDS[rank]}
                  </option>
                ))}
              </select>
            </label>
            <Field
              label="Only rows where"
              data-role="editor-where"
              value={draft.where ?? ''}
              placeholder="For example: net > 0"
              onChange={(e) => {
                const where = e.target.value
                // Cleared as null where it was saved, so the engine shows every row again.
                setDraft({
                  ...draft,
                  where: where.trim() ? where : initial.where ? null : undefined,
                })
              }}
            />
            <Field
              label="Show at most"
              type="number"
              min={1}
              max={50}
              data-role="editor-limit"
              value={draft.limit ?? ''}
              placeholder="All"
              onChange={(e) => {
                const n = Number(e.target.value)
                setDraft({
                  ...draft,
                  limit:
                    e.target.value === '' || !Number.isInteger(n) || n < 1
                      ? initial.limit
                        ? null
                        : undefined
                      : Math.min(n, 50),
                })
              }}
            />
          </div>
        ) : null}
        {vocabulary?.time ? (
          <TimeFields
            draft={draft}
            time={vocabulary.time}
            wasWindowed={wasWindowed}
            onChange={setDraft}
          />
        ) : null}
        <Field
          label="Section"
          value={draft.section ?? ''}
          maxLength={40}
          placeholder="None"
          onChange={(e) => {
            setDraft({ ...draft, section: e.target.value || null })
          }}
        />
        {refused ? (
          <p role="alert" className="text-body-s text-destructive">
            {refused}
          </p>
        ) : null}
      </form>
    </Modal>
  )
}
